import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

export default function LegalPage({ title, accent, updated, intro, sections, testId }) {
  useEffect(() => {
    document.title = `${title} ${accent} | Modi Marine`;
  }, [title, accent]);

  return (
    <section className="bg-navy px-6 pb-20 pt-32 lg:px-12 lg:pb-28 lg:pt-40" data-testid={testId}>
      <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-12 lg:gap-20">
        <aside className="lg:col-span-4">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="mb-6 text-[10px] uppercase tracking-[0.45em] text-gold sm:text-xs">
            Legal
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }} className="font-serif text-4xl font-light leading-[1.02] tracking-tight text-navy-ink sm:text-5xl lg:text-6xl" data-testid={`${testId}-heading`}>
            {title}
            <span className="italic text-gold-light"> {accent}</span>
          </motion.h1>
          <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-mist" data-testid={`${testId}-updated`}>Last updated · {updated}</p>
          <nav className="mt-10 hidden flex-col gap-3 border-l border-navy-ink/10 pl-5 lg:flex" data-testid={`${testId}-toc`}>
            {sections.map((s, i) => (
              <a key={s.heading} href={`#${s.id}`} className="text-[11px] uppercase tracking-[0.22em] text-mist transition-colors hover:text-gold">
                {String(i + 1).padStart(2, "0")} — {s.heading}
              </a>
            ))}
          </nav>
          <Link to="/enquire" className="mt-10 inline-block border border-gold/60 px-6 py-3 text-[11px] uppercase tracking-[0.28em] text-gold transition-colors hover:bg-gold hover:text-navy" data-testid={`${testId}-contact-link`}>
            Questions? Contact us
          </Link>
        </aside>

        <motion.article initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }} className="lg:col-span-8">
          <p className="max-w-3xl text-base font-light leading-relaxed text-navy-ink/80" data-testid={`${testId}-intro`}>{intro}</p>
          <div className="mt-12 space-y-12">
            {sections.map((s, i) => (
              <section key={s.heading} id={s.id} className="scroll-mt-32 border-t border-navy-ink/10 pt-8" data-testid={`${testId}-section-${s.id}`}>
                <p className="text-[10px] uppercase tracking-[0.35em] text-gold">{String(i + 1).padStart(2, "0")}</p>
                <h2 className="mt-3 font-serif text-2xl font-light text-navy-ink sm:text-3xl">{s.heading}</h2>
                <div className="mt-5 max-w-3xl space-y-4 text-sm font-light leading-relaxed text-mist sm:text-base">
                  {s.body.map((p, j) =>
                    Array.isArray(p) ? (
                      <ul key={j} className="list-none space-y-2 pl-0">
                        {p.map((li) => (
                          <li key={li} className="flex gap-3"><span className="mt-2 h-px w-4 shrink-0 bg-gold" />{li}</li>
                        ))}
                      </ul>
                    ) : (
                      <p key={j}>{p}</p>
                    )
                  )}
                </div>
              </section>
            ))}
          </div>
        </motion.article>
      </div>
    </section>
  );
}
